/**
 * ConversationFeed - Feed-like container for conversation cards
 * Social finance platform feel with scrollable feed
 */
import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConversationCard from './ConversationCard';
import { isAdviceCard } from './CardSaveButton';

const ConversationFeed = ({ 
  cards, 
  user, 
  section = 'cashflow',
  onSaveNote,
  loading = false,
  visionId = null,
  renderAfterCard = null,
}) => {
  const firstAdviceIndex = cards.findIndex((c) => isAdviceCard(c));
  const feedRef = useRef(null);

  const embeddedPanel = section === 'copilot' || section === 'cashflow';

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [cards, loading]);

  return (
    <div
      ref={feedRef}
      className={`flex-1 min-h-0 overflow-y-auto space-y-4 px-4 py-3 ${
        embeddedPanel ? '' : 'pr-2'
      }`}
      style={
        embeddedPanel
          ? undefined
          : { maxHeight: section === 'vision' ? 'calc(100vh - 20rem)' : 'calc(100vh - 24rem)' }
      }
    >
      <AnimatePresence>
        {cards.map((card, index) => (
          <React.Fragment key={card.id || index}>
            <ConversationCard
              card={card}
              index={index}
              user={user}
              section={section}
              visionId={visionId}
              showSaveHint={index === firstAdviceIndex}
              onSaveNote={onSaveNote}
            />
            {renderAfterCard?.(card, index)}
          </React.Fragment>
        ))}
      </AnimatePresence>

      {/* Loading Indicator */}
      {loading && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className={
            section === 'cashflow'
              ? 'rounded-lg border border-on-dark-muted/30 p-4'
              : 'aura-card light-surface p-4'
          }
        >
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              <div
                className="w-2 h-2 rounded-full animate-pulse bg-[color:var(--text-secondary)]"
                style={{ animationDelay: '0ms' }}
              />
              <div
                className="w-2 h-2 rounded-full animate-pulse bg-[color:var(--text-secondary)]"
                style={{ animationDelay: '150ms' }}
              />
              <div
                className="w-2 h-2 rounded-full animate-pulse bg-[color:var(--text-secondary)]"
                style={{ animationDelay: '300ms' }}
              />
            </div>
            <p className="text-body text-secondary">
              Aura is thinking…
            </p>
          </div>
        </motion.div>
      )}

      {/* Empty State */}
      {!loading && cards.length === 0 && (
        <div className="text-center py-12 light-surface">
          <div className="w-12 h-12 bg-pine rounded-md flex items-center justify-center mx-auto mb-4">
            <span className="text-primary text-label font-medium">Aura</span>
          </div>
          <p className="text-body text-primary mb-2">Start a conversation</p>
          <p className="text-label text-secondary">Ask Aura anything about this vision</p>
        </div>
      )}
    </div>
  );
};

export default ConversationFeed;

